<modal-panel title="Create New Booking" (close)="close()">
  <form [formGroup]="form">
    <mat-form-field>
      <mat-label>Submit To</mat-label>
      <mat-select formControlName="submitToCode">
        @for (item of (submitTo$ | async)?.items; track $index) {
          <mat-option [value]="item.submitToLinkCode">{{ item.submitToLink }}</mat-option>
        }
      </mat-select>
      <mat-error>Please choose an option</mat-error>
    </mat-form-field>

    <mat-form-field>
      <mat-label>Ticket Number</mat-label>
      <input matInput formControlName="startingTicketNo" maxlength="32"/>
    </mat-form-field>

    <mat-form-field>
      <mat-label>Travel Type</mat-label>
      <mat-select formControlName="travelTypeId" (selectionChange)="onTravelTypeChange($event.value)">
        @for (item of (travelType$ | async); track $index) {
          <mat-option [value]="item.id">{{ item.travelTypeName }}</mat-option>
        }
      </mat-select>
      <mat-error>Please choose an option</mat-error>
    </mat-form-field>

    <mat-form-field>
      <mat-label>External Travel Type</mat-label>
      <mat-select formControlName="externalTravelServiceTypeId">
        @for (item of (externalTravelServiceType$ | async); track $index) {
          <mat-option [value]="item.id">{{ item.name }}</mat-option>
        }
      </mat-select>
      <mat-error>Please choose an option</mat-error>
    </mat-form-field>

    <mat-form-field>
      <mat-label>Confirmation Number</mat-label>
      <input matInput formControlName="confirmationNumber" maxlength="32"/>
    </mat-form-field>

    <mat-form-field>
      <mat-label>Total Passengers</mat-label>
      <input
        matInput
        formControlName="numberOfPax"
        type="number"
        min="0"
        max="150"
        (keydown)="preventDecimal($event)"/>
      @if (form.controls.numberOfPax.hasError('required')) {
        <mat-error>Total passengers is required</mat-error>
      }
      @if (form.controls.numberOfPax.hasError('min')) {
        <mat-error>Total passengers must be greater than 0</mat-error>
      }
      @if (form.controls.numberOfPax.hasError('max')) {
        <mat-error>Total passengers must be less than 150</mat-error>
      }
    </mat-form-field>

    <mat-form-field>
      <mat-label>Vendor</mat-label>
      <input matInput [formControl]="vendorControl" [matAutocomplete]="autocomplete1"/>
      <mat-autocomplete #autocomplete1="matAutocomplete" [displayWith]="displayVendorFn">
        @for (item of (filteredVendorOptions$ | async); track $index) {
          <mat-option [value]="item">{{ item.vendorName }}</mat-option>
        }
      </mat-autocomplete>
      @if (vendorControl.value) {
        <button matSuffix mat-icon-button (click)="resetFilter('vendor'); $event.stopPropagation()">
          <mat-icon>close</mat-icon>
        </button>
      }
      @if (vendorControl.hasError('invalidModel')) {
        <mat-error>Type and select an option</mat-error>
      }
    </mat-form-field>

    <mat-form-field>
      <mat-label>Depart Date</mat-label>
      <input matInput readonly [matDatepicker]="picker1" formControlName="departDate"/>
      <mat-hint>MM/DD/YYYY</mat-hint>
      @if (departDateControl.value) {
        <button matSuffix mat-icon-button type="button" (click)="resetFilter('departDate')">
          <mat-icon>close</mat-icon>
        </button>
      } @else {
        <mat-datepicker-toggle matIconSuffix [for]="picker1"/>
      }
      <mat-datepicker #picker1/>
      @if (departDateControl.hasError('required')) {
        <mat-error>Depart date is required</mat-error>
      }
    </mat-form-field>

    <mat-form-field>
      <mat-label>Return Date</mat-label>
      <input
        matInput
        readonly
        [matDatepicker]="picker2"
        [matDatepickerFilter]="returnDateFilter"
        formControlName="returnDate"/>
      <mat-hint>MM/DD/YYYY</mat-hint>
      @if (returnDateControl.value) {
        <button matSuffix mat-icon-button type="button" (click)="resetFilter('returnDate')">
          <mat-icon>close</mat-icon>
        </button>
      } @else {
        <mat-datepicker-toggle matIconSuffix [for]="picker2"/>
      }
      <mat-datepicker #picker2/>
      @if (returnDateControl.hasError('required')) {
        <mat-error>Return date is required</mat-error>
      }
      @if (returnDateControl.hasError('invalidDate')) {
        <mat-error>Return date must be greater than depart date</mat-error>
      }
    </mat-form-field>

    <mat-form-field>
      <mat-label>Agent</mat-label>
      <input matInput [formControl]="agentControl" [matAutocomplete]="autocomplete2"/>
      <mat-autocomplete #autocomplete2="matAutocomplete" [displayWith]="displayAgentFn">
        @for (item of (filteredAgentOptions$ | async); track $index) {
          <mat-option [value]="item">{{ item.agentName }}</mat-option>
        }
      </mat-autocomplete>
      @if (agentControl.value) {
        <button matSuffix mat-icon-button (click)="resetFilter('agent'); $event.stopPropagation()">
          <mat-icon>close</mat-icon>
        </button>
      }
      @if (agentControl.hasError('invalidModel')) {
        <mat-error>Type and select an option</mat-error>
      }
    </mat-form-field>

    <mat-form-field>
      <mat-label>Total Fare</mat-label>
      <input
        matInput
        preventNumber
        formControlName="totalFare"
        type="number"
        (keydown)="validateMaxDecimals($event)"/>
      <mat-error>Total fare is required</mat-error>
    </mat-form-field>

    <mat-form-field>
      <mat-label>Commission Rate</mat-label>
      <input
        matInput
        preventNumber
        formControlName="commRate"
        type="number"
        (keydown)="validateMaxDecimals($event)"/>
      <mat-error>Commission rate is required</mat-error>
    </mat-form-field>

    <mat-form-field>
      <mat-label>Currency</mat-label>
      <mat-select formControlName="currency">
        @for (item of (currencies$ | async)?.items; track $index) {
          <mat-option [value]="item">
            {{ item?.currencyName }} {{ item?.symbol ? '(' + item?.symbol + ')' : '' }}
          </mat-option>
        }
      </mat-select>
      <mat-error>Please choose an option</mat-error>
    </mat-form-field>

    <mat-form-field>
      <mat-label>Commission Amount</mat-label>
      <input
        matInput
        preventNumber
        formControlName="commAmt"
        type="number"
        (keydown)="validateMaxDecimals($event)"/>
      <mat-error>Commission amount is required</mat-error>
    </mat-form-field>

    <mat-form-field>
      <mat-label>Number Of Rooms</mat-label>
      <input
        matInput
        formControlName="numberOfRooms"
        type="number"
        min="0"
        max="150"
        (keydown)="preventDecimal($event)"/>
      @if (form.controls.numberOfRooms.hasError('required')) {
        <mat-error>Number of rooms is required</mat-error>
      }
      @if (form.controls.numberOfRooms.hasError('min')) {
        <mat-error>Number of rooms must be greater than 0</mat-error>
      }
      @if (form.controls.numberOfRooms.hasError('max')) {
        <mat-error>Number of rooms must be less than 150</mat-error>
      }
    </mat-form-field>

    <mat-form-field>
      <mat-label>Indicator</mat-label>
      <mat-select formControlName="interCode">
        @for (item of indicators(); track $index) {
          <mat-option [value]="item.value">{{ item.displayName }}</mat-option>
        }
      </mat-select>
      <mat-error>Please choose an option</mat-error>
    </mat-form-field>
  </form>

  <ng-container modalPanelFooter>
    <button mat-raised-button color="primary" (click)="apply()" [disabled]="form.invalid || loading()">
      @if (!loading()) {
        <span>Apply</span>
      } @else {
        <mat-spinner color="accent" diameter="30"/>
      }
    </button>
    <button mat-raised-button color="warn" (click)="close()">Close</button>
  </ng-container>
</modal-panel>
